import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { FiCalendar, FiClock, FiUser, FiTruck, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import BookingDetailModal from '../../components/BookingDetailModal';

const MechanicJobs = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);


  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the new mechanic-tasks API
      let response;

      if (filter === 'all') {
        response = await API.get('/api/mechanic-tasks/all');
      } else {
        response = await API.get('/api/mechanic-tasks/all', {
          params: { status: filter }
        });
      }

      console.log('Mechanic tasks data:', response.data);

      if (!response.data || response.data.length === 0) {
        console.log('No tasks found');
        setBookings([]);
        setLoading(false);
        return;
      }

      // Set today's date

      // Format bookings data from tasks
      const formattedBookings = response.data.map(task => {
        return {
          id: task.id,
          service_name: task.service_name || 'Layanan tidak tersedia',
          booking_date: task.booking_date || 'Tanggal tidak tersedia',
          booking_time: task.booking_time || 'Waktu tidak tersedia',
          task_status: task.task_status || task.booking_status || 'pending',
          customer_name: task.customer_name || 'Pelanggan',
          make: task.make || 'N/A',
          model: task.model || '',
          license_plate: task.license_plate || 'Tidak tersedia',
          estimated_time: task.estimated_time || '60',
          customer_notes: task.customer_notes || '',
          start_time: task.start_time,
          end_time: task.end_time,
          task_id: task.task_id || task.id
        };
      });

      console.log('Formatted mechanic tasks:', formattedBookings);
      setBookings(formattedBookings);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching bookings:', err);

      // Extract error message from API response or use a default message
      const errorMessage = err.response?.data?.message || 'Gagal memuat daftar booking';
      setError(errorMessage);

      // Set empty bookings array to avoid undefined issues
      setBookings([]);
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const fetchTodayBookings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the daily tasks endpoint
      const response = await API.get('/api/mechanic-tasks/daily');

      console.log('Today\'s mechanic tasks data:', response.data);

      if (!response.data || !response.data.tasks || response.data.tasks.length === 0) {
        console.log('No tasks found for today');
        setBookings([]);
        setLoading(false);
        return;
      }

      // Set today's date

      // Format bookings data from tasks
      const formattedBookings = response.data.tasks.map(task => {
        return {
          id: task.id,
          service_name: task.service_name || 'Layanan tidak tersedia',
          booking_date: task.booking_date || 'Tanggal tidak tersedia',
          booking_time: task.booking_time || 'Waktu tidak tersedia',
          task_status: task.task_status || task.booking_status || 'pending',
          customer_name: task.customer_name || 'Pelanggan',
          make: task.make || 'N/A',
          model: task.model || '',
          license_plate: task.license_plate || 'Tidak tersedia',
          estimated_time: task.estimated_time || '60',
          customer_notes: task.customer_notes || '',
          field_notes: task.field_notes || '',
          start_time: task.start_time,
          end_time: task.end_time,
          task_id: task.task_id || task.id
        };
      });

      console.log('Formatted today\'s tasks:', formattedBookings);
      setBookings(formattedBookings);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching today\'s bookings:', err);

      // Extract error message from API response or use a default message
      const errorMessage = err.response?.data?.message || 'Gagal memuat daftar booking hari ini';
      setError(errorMessage);

      // Set empty bookings array to avoid undefined issues
      setBookings([]);
      setLoading(false);
    }
  };

  const filteredBookings = filter === 'all'
    ? bookings
    : bookings.filter(booking => booking.task_status === filter);

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      // We can now use the booking ID directly for the status update
      // The backend will handle creating a task if needed
      const response = await API.put(`/api/mechanic-tasks/${bookingId}/status`, {
        status: newStatus
      });

      console.log('Status update response:', response.data);

      // Update local state
      setBookings(bookings.map(booking =>
        booking.id === bookingId ? { ...booking, task_status: newStatus } : booking
      ));

      // Close modal if open
      if (showModal && selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, task_status: newStatus });
      }
    } catch (err) {
      console.error('Error updating booking status:', err);

      // Extract error message from API response or use a default message
      const errorMessage = err.response?.data?.message || 'Gagal mengubah status pekerjaan';
      alert(errorMessage);
    }
  };

  const handleOpenDetails = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
  };

  const getStatusBadge = (status) => {
    const badgeClasses = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in-progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200',
    };

    const statusText = {
      'pending': 'Menunggu',
      'in-progress': 'Sedang Dikerjakan',
      'completed': 'Selesai',
      'cancelled': 'Dibatalkan'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${badgeClasses[status] || 'bg-gray-100'}`}>
        {statusText[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout userRole="mechanic" userName="Montir">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="mechanic" userName="Montir">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Daftar Booking</h1>
          <p className="text-gray-600">Kelola semua pekerjaan yang dipesan pelanggan</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={fetchTodayBookings}
            className="flex items-center bg-green-500 text-white px-4 py-2 rounded-md"
          >
            <FiCalendar className="mr-2" /> Tugas Hari Ini
          </button>
          <button
            onClick={fetchBookings}
            className="flex items-center bg-primary text-white px-4 py-2 rounded-md"
          >
            <FiRefreshCw className="mr-2" /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <FiAlertCircle className="mr-2" />
          <span>{error}</span>
        </div>
      )}

      {/* Filter */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-md ${filter === 'all' ? 'bg-primary text-white' : 'bg-white border border-gray-300'}`}
        >
          Semua
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-md ${filter === 'pending' ? 'bg-primary text-white' : 'bg-white border border-gray-300'}`}
        >
          Menunggu
        </button>
        <button
          onClick={() => setFilter('in-progress')}
          className={`px-4 py-2 rounded-md ${filter === 'in-progress' ? 'bg-primary text-white' : 'bg-white border border-gray-300'}`}
        >
          Sedang Dikerjakan
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`px-4 py-2 rounded-md ${filter === 'completed' ? 'bg-primary text-white' : 'bg-white border border-gray-300'}`}
        >
          Selesai
        </button>
      </div>

      {/* Booking Cards */}
      {filteredBookings.length === 0 ? (
        <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-500">
          Tidak ada booking ditemukan
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBookings.map(booking => (
            <div key={booking.id} className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="p-4 border-b">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-lg">{booking.service_name}</h3>
                  {getStatusBadge(booking.task_status)}
                </div>
                <div className="flex items-center text-gray-600 mb-1">
                  <FiCalendar className="mr-2" />
                  <span>{booking.booking_date}</span>
                </div>
                <div className="flex items-center text-gray-600 mb-1">
                  <FiClock className="mr-2" />
                  <span>{booking.booking_time}</span>
                </div>
                <div className="flex items-center text-gray-600 mb-1">
                  <FiUser className="mr-2" />
                  <span>{booking.customer_name}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FiTruck className="mr-2" />
                  <span>{booking.make} {booking.model} ({booking.license_plate})</span>
                </div>
              </div>
              <div className="p-4 bg-gray-50 flex justify-between items-center">
                <button
                  onClick={() => handleOpenDetails(booking)}
                  className="text-primary hover:underline"
                >
                  Lihat Detail
                </button>
                <div>
                  {booking.task_status === 'pending' && (
                    <button
                      onClick={() => handleStatusChange(booking.id, 'in-progress')}
                      className="bg-blue-500 text-white px-3 py-1 rounded-md text-sm"
                    >
                      Mulai
                    </button>
                  )}
                  {booking.task_status === 'in-progress' && (
                    <button
                      onClick={() => handleStatusChange(booking.id, 'completed')}
                      className="bg-green-500 text-white px-3 py-1 rounded-md text-sm"
                    >
                      Selesai
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showModal && selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={closeModal}
          onStatusChange={handleStatusChange}
        />
      )}
    </DashboardLayout>
  );
};

export default MechanicJobs;
